import {
  useCallback, useEffect, useMemo,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { useAjaxCall } from '~/_shared/utils/hooks/useAjaxCall';
import type { BoundaryGroupId } from '~/store/boundaries/boundaryIdentifier.type';
import {
  mapSettingsDataLayersAddDataLayer, mapSettingsDataLayersUpdateBoundaryGroup, mapSettingsDataLayersUpdateDataLayer,
} from '~/store/mapSettings/data/dataLayers/mapSettingsDataLayers.actionCreators';
import { createDataLayerFromTemplate } from '~/store/mapSettings/data/dataLayers/mapSettingsDataLayers.factory';
import type { DataLayerGroup } from '~/store/mapSettings/data/dataLayers/mapSettingsDataLayers.state';
import { useClientIdSelector } from '~/store/selectors/useClientIdSelector';
import { onDataLayerTemplatesChanged } from './dataLayers.actionCreators';
import {
  createDataLayerTemplate,
  type DataLayerTemplate,
  type DataLayerTemplateId,
  deleteDataLayerTemplate,
  getDataLayerTemplates,
  updateDataLayerTemplate,
} from './dataLayers.repository';
import { useDataLayersTemplatesEtagSelector } from './dataLayers.selectors';

const EMPTY_TEMPLATES: ReadonlyArray<DataLayerTemplate> = [];

export const useDataLayersTemplates = () => {
  const dispatch = useDispatch();
  const clientId = useClientIdSelector();
  const dataLayersEtag = useDataLayersTemplatesEtagSelector();

  const [hasRequestedFetchTemplates, setHasRequestedFetchTemplates] = useState(false);

  const fetchTemplates = useCallback(() => {
    if (clientId) {
      return getDataLayerTemplates(clientId);
    }
    return Promise.resolve({ list: EMPTY_TEMPLATES });
  }, [clientId]);

  const { isLoading: isFetchLoading, error: fetchError, data: fetchData, invokeAjax: fetchInvokeAjax } = useAjaxCall(fetchTemplates);
  const { isLoading: isCreateLoading, invokeAjax: create } = useAjaxCall(createDataLayerTemplate);
  const { isLoading: isUpdateLoading, invokeAjax: update } = useAjaxCall(updateDataLayerTemplate);
  const { isLoading: isDeleteLoading, invokeAjax: remove } = useAjaxCall(deleteDataLayerTemplate);

  const templates = useMemo(() => fetchData?.list ?? EMPTY_TEMPLATES, [fetchData]);

  const createNewTemplate = useCallback((dataLayer: DataLayerGroup) => {
    if (!clientId) {
      return Promise.reject();
    }

    const { id: _id, fromTemplate: _fromTemplate, ...dataLayerTemplateData } = dataLayer;
    return create(clientId, dataLayerTemplateData)
      .then(response => {
        dispatch(mapSettingsDataLayersUpdateDataLayer({
          id: dataLayer.id,
          fromTemplate: response.template.id,
        }));
        dispatch(onDataLayerTemplatesChanged());
        return response;
      });
  }, [clientId, create, dispatch]);

  const updateDataLayerTemplateById = useCallback((templateId: DataLayerTemplateId, dataLayer: DataLayerGroup) => {
    if (!clientId) {
      return Promise.reject();
    }
    const { id: _id, fromTemplate: _fromTemplate, ...dataLayerTemplateData } = dataLayer;
    return update(clientId, templateId, dataLayerTemplateData)
      .then(response => {
        dispatch(onDataLayerTemplatesChanged());
        return response;
      });
  }, [clientId, update, dispatch]);

  const deleteDataLayerTemplateById = useCallback((templateId: DataLayerTemplateId) => {
    if (!clientId) {
      return Promise.reject();
    }
    return remove(clientId, templateId)
      .then(response => {
        dispatch(onDataLayerTemplatesChanged());
        return response;
      });
  }, [clientId, remove, dispatch]);

  const createNewDataLayerFromTemplate = useCallback((dataLayerTemplate: DataLayerTemplate, boundaryGroupId: BoundaryGroupId) => {
    const newLayer = createDataLayerFromTemplate(dataLayerTemplate);
    dispatch(mapSettingsDataLayersUpdateBoundaryGroup(newLayer.id, boundaryGroupId));
    dispatch(mapSettingsDataLayersAddDataLayer(newLayer));
  }, [dispatch]);

  const requestFetchTemplates = useCallback(() => {
    setHasRequestedFetchTemplates(true);
  }, []);

  useEffect(() => {
    if (clientId && hasRequestedFetchTemplates) {
      fetchInvokeAjax();
    }
  }, [clientId, fetchInvokeAjax, hasRequestedFetchTemplates, dataLayersEtag]);

  return {
    templates,
    fetchDataLayerTemplates: requestFetchTemplates,
    createNewTemplate,
    createNewDataLayerFromTemplate,
    updateDataLayerTemplate: updateDataLayerTemplateById,
    deleteDataLayerTemplate: deleteDataLayerTemplateById,
    isLoading: isFetchLoading || isCreateLoading || isUpdateLoading || isDeleteLoading,
    fetchError,
  };
};
